import { AxiosPromise } from 'axios';
import {
  sendEvidentlyMetrics,
  SendEvidentlyMetricsParams,
  trackEvidentlyMetrics,
  TrackEvidentlyMetricsParams,
  trackEvidentlyMultipleMetrics,
} from '../sdk/CommonSDK';
import { EvidentlyFeatureDetails } from '../reducers/types/Common.d';
import { getLocalItem } from '../common';
import { getEvidentlyExperiment } from '../sdk';
import store from '../store';
import { passwordlessFeatureName } from '../utils/evidentlyFeatures/passwordlessEvaluation';

export type NewCreditOfferVariations = 'Control' | 'IntroCreditOffer' | 'NoIntroOffer';

export const evidentlyEnabled = (): boolean => (store && store.getState()?.settings?.is_evidently_enabled) ?? false;

export const RULE_MATCH_REASON = 'EXPERIMENT_RULE_MATCH';
export const DEFAULT_REASON = 'DEFAULT';

export type EvidentlyFeatureExperiment = {
  reason: string;
  userId: string;
  variation: string;
};

export const enum EVIDENTLY_EXPERIMENTS {}

/**
 * NOTE: Don't use const enum
 * Need to access key
 * Else will get error:
 * 'const' enums can only be used in property or index access expressions
 * or the right hand side
 * of an import declaration or export assignment or type query.
 * ref: https://www.typescriptlang.org/docs/handbook/enums.html
 */
export enum EVIDENTLY_FEATURES {
  PASSWORDLESS = 'PasswordlessLogin',
}

export const enum EVIDENTLY_FEATURE_VARIATIONS {
  CONTROL = 'Control',
  NEW_FEATURE = 'NewFeature',
}
/**
 * NOTE: Don't use const enum
 * Need to access key
 * Else will get error:
 * 'const' enums can only be used in property or index access expressions
 * or the right hand side
 * of an import declaration or export assignment or type query.
 * ref: https://www.typescriptlang.org/docs/handbook/enums.html
 */
export enum EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS {
  MANDATORY_STEP_IN_REGISTRATION = 'evidently_registration_variation',
}

export const EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES = {
  [passwordlessFeatureName]: 'evidently_passwordless',
};

// in seconds
export const EVIDENTLY_METRICS_TIME_SESSIONS = {};

export const sendEvidentlyMetric = (
  feature: EvidentlyFeatureDetails,
  details: SendEvidentlyMetricsParams['details']
): void | AxiosPromise => {
  if (!evidentlyEnabled || !feature) return;
  const { userId } = feature;

  return sendEvidentlyMetrics({ userId, details });
};

export const enum OffersMetrics {
  OFFER_ACCEPTED = 'newOfferPageOffersAccepted',
}

export async function isEvidentlyExperimentLive(experiment: string): Promise<boolean> {
  if (!evidentlyEnabled) {
    return false;
  }

  try {
    const res = await getEvidentlyExperiment(experiment);
    const { isLive } = res.data;

    return isLive;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false;
  }
}

export const trackEvidentlyMetric = async (
  localStorageKey: EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS,
  formData: TrackEvidentlyMetricsParams<KeyType>[]
): Promise<void> => {
  if (!evidentlyEnabled) return;

  const feature = JSON.parse(getLocalItem(localStorageKey));

  if (feature === null) {
    return;
  }

  if (feature.reason === DEFAULT_REASON) {
    return;
  }

  await trackEvidentlyMetrics({
    ...formData,
    hashId: feature.userId,
  });
};

export const trackEvidentlyMultipleMetric = async <KeysType>(
  localStorageKey: EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS,
  formData: TrackEvidentlyMetricsParams<KeysType>[]
): Promise<void> => {
  if (!evidentlyEnabled) return;

  const feature = JSON.parse(getLocalItem(localStorageKey));

  if (feature === null) {
    return;
  }

  if (feature.reason === DEFAULT_REASON) {
    return;
  }

  const metrics: TrackEvidentlyMetricsParams<KeysType>[] = formData.map(data => {
    return {
      ...data,
      hashId: data.hashId || feature.userId,
    };
  });

  await trackEvidentlyMultipleMetrics(metrics);
};

export const showNewVariation = (evidently: any, feature: any): string | null => {
  if (!evidently || !(feature in evidently) || evidently[feature] === 'undefined') return null;

  const { variation } = evidently[feature];
  const localStorageVariation = getLocalItem(EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES[feature]);
  const bypassVariation = localStorageVariation ? JSON.parse(localStorageVariation) : null;

  return bypassVariation ? bypassVariation.variation : variation;
};

export const getFeatureDetails = (evidently: any, feature: any): null | EvidentlyFeatureExperiment => {
  if (!evidently || !(feature in evidently) || evidently[feature] === 'undefined') return null;

  const localStorageVariation = getLocalItem(EVIDENTLY_FEATURE_LOCAL_STORAGE_KEYS_BASED_ON_FEATURES[feature]);
  const bypassVariation = localStorageVariation ? JSON.parse(localStorageVariation) : null;

  return bypassVariation ? bypassVariation : evidently[feature];
};
